import React, { createContext, useContext, useState, useMemo } from 'react'
import { useCallback } from 'react'
import { MEDICAL_ALERTS } from '@/v2/constants'

export const PatientContext = createContext([])

const parse = value => parseInt(value, 10)

export const PatientProvider = ({ children }) => {
  const [patient, setPatient] = useState({})
  const [progressions, setProgressions] = useState([])
  const [series, setSeries] = useState([])
  const [officeNotes, setOfficeNotes] = useState([])
  const [notes, setNotes] = useState([])
  const [medicalAlerts, setMedicalAlerts] = useState([])

  const handleSetPatient = useCallback(newPatient => {
    if (!newPatient || !newPatient?.id) return

    setPatient(old => {
      // Compare the MERGED result against the current patient, not the incoming
      // partial. Partial updates (e.g. the profile payload) never match `old`
      // by themselves, so the old check always produced a new object reference
      // and churned the context on every call. Returning `old` when nothing
      // changed keeps the reference stable and avoids re-render storms.
      const merged = { ...old, ...newPatient }
      if (JSON.stringify(old) === JSON.stringify(merged)) {
        return old
      }
      return merged
    })

    if (newPatient.hasOwnProperty('premedication')) {
      const nextAlerts = MEDICAL_ALERTS.filter(item => newPatient[item.name])
      setMedicalAlerts(old =>
        JSON.stringify(old) === JSON.stringify(nextAlerts) ? old : nextAlerts
      )
    }
  }, [])

  const handleSetProgressions = useCallback(progressions => {
    setProgressions(progressions)
  }, [])

  const handleSetSeries = useCallback(series => {
    setSeries(series)
  }, [])

  const handleSetNotes = useCallback(notes => {
    setNotes(notes)
  }, [])

  const handleAddProgressionSeries = useCallback((series, progressionId) => {
    setProgressions(currentProgressions =>
      currentProgressions.map(progression =>
        parse(progression.id) === parse(progressionId)
          ? { ...progression, series }
          : { ...progression }
      )
    )
  }, [])

  const handleAddProgression = useCallback(progression => {
    setProgressions(state => [...state, progression])
  }, [])

  const handleRenameProgression = useCallback(progression => {
    setProgressions(currentProgressions =>
      currentProgressions.map(item =>
        parse(item.id) === parse(progression.id)
          ? { ...item, name: progression.name }
          : item
      )
    )
  }, [])

  const handleDeleteProgression = useCallback(progression => {
    setProgressions(currentProgressions =>
      currentProgressions.filter(
        ({ id }) => parse(id) !== parse(progression.id)
      )
    )
  }, [])

  const handleUploadImage = useCallback((progressionId, seriesId, image) => {
    setProgressions(currentProgressions =>
      currentProgressions.map(progression => {
        return parse(progression.id) === parse(progressionId)
          ? {
              ...progression,
              series: progression.series.map(serie => {
                if (parse(serie.id) === parse(seriesId)) {
                  const findImageByPosition = serie.images.find(
                    item => item.position === image.position
                  )
                  if (findImageByPosition) {
                    return {
                      ...serie,
                      images: serie.images.map(item =>
                        item.position === image.position ? image : item
                      ),
                    }
                  }

                  return {
                    ...serie,
                    images: [...serie.images, image],
                  }
                }

                return { ...serie }
              }),
            }
          : { ...progression }
      })
    )
  }, [])

  const handleDeleteImage = useCallback((progressionId, seriesId, imageId) => {
    setProgressions(currentProgressions =>
      currentProgressions.map(progression => {
        return parse(progression.id) === parse(progressionId)
          ? {
              ...progression,
              series: progression.series.map(serie =>
                parse(serie.id) === parse(seriesId)
                  ? {
                      ...serie,
                      images: serie.images.filter(
                        image => parse(image.id) !== parse(imageId)
                      ),
                    }
                  : { ...serie }
              ),
            }
          : { ...progression }
      })
    )
  }, [])
  const handleAddNote = useCallback(note => {
    setNotes(currentNotes => [...currentNotes, note])
  }, [])

  const handleUpdateNote = useCallback(note => {
    setNotes(currentNotes =>
      currentNotes.map(item =>
        item.id === note.id ? { ...note } : { ...item }
      )
    )
  }, [])

  const handleDeleteNote = useCallback(noteId => {
    setNotes(currentNotes => currentNotes.filter(({ id }) => id !== noteId))
  }, [])

  const patientProfile = useMemo(
    () => ({
      patient,
      progressions,
      series,
      notes,
      officeNotes,
      medicalAlerts,
      onSetPatient: handleSetPatient,
      onSetProgressions: handleSetProgressions,
      onSetSeries: handleSetSeries,
      onSetNotes: handleSetNotes,
      onSetOfficeNotes: setOfficeNotes,
      onAddProgressionSeries: handleAddProgressionSeries,
      onAddProgression: handleAddProgression,
      onRenameProgression: handleRenameProgression,
      onDeleteProgression: handleDeleteProgression,
      onUploadImage: handleUploadImage,
      onDeleteImage: handleDeleteImage,
      onAddNote: handleAddNote,
      onUpdateNote: handleUpdateNote,
      onDeleteNote: handleDeleteNote,
    }),
    [
      patient,
      progressions,
      series,
      notes,
      officeNotes,
      medicalAlerts,
      handleSetPatient,
      handleSetProgressions,
      handleSetSeries,
      handleSetNotes,
      setOfficeNotes,
      handleAddProgressionSeries,
      handleAddProgression,
      handleRenameProgression,
      handleDeleteProgression,
      handleUploadImage,
      handleDeleteImage,
      handleAddNote,
      handleUpdateNote,
      handleDeleteNote,
    ]
  )
  return (
    <PatientContext.Provider value={patientProfile}>
      {children}
    </PatientContext.Provider>
  )
}

export const usePatientContext = () => {
  const {
    patient,
    progressions,
    series,
    notes,
    officeNotes,
    medicalAlerts,
    onSetPatient,
    onSetProgressions,
    onSetSeries,
    onSetNotes,
    onSetOfficeNotes,
    onAddProgressionSeries,
    onAddProgression,
    onRenameProgression,
    onDeleteProgression,
    onUploadImage,
    onDeleteImage,
    onAddNote,
    onUpdateNote,
    onDeleteNote,
  } = useContext(PatientContext)

  return {
    patient,
    progressions,
    series,
    notes,
    officeNotes,
    medicalAlerts,
    onSetPatient,
    onSetProgressions,
    onSetSeries,
    onSetNotes,
    onSetOfficeNotes,
    onAddProgressionSeries,
    onAddProgression,
    onRenameProgression,
    onDeleteProgression,
    onUploadImage,
    onDeleteImage,
    onAddNote,
    onUpdateNote,
    onDeleteNote,
  }
}
